<template>
    <div class="row q-col-gutter-md">
        <div class="col-xs-12 q-mb-none">
            <info-box :type="notificationSettingsInfo.type" no-margin>
                <p v-if="contact.consultingSettings.has_active_user">{{ $tc('views.contact.settings.consulting-settings.notification-settings.default-email-address--info', 1) }}</p>

                <template v-else>
                    <p><strong>{{ $tc(`views.contact.settings.consulting-settings.notification-settings.notifications-sent-info--${notificationSettingsInfo.notificationsSent}`, 1) }}</strong></p>
                    <p>{{ $tc('views.contact.settings.consulting-settings.notification-settings.no-linked-user--info', 1) }}</p>
                    <ul>
                        <li>
                            <q-icon
                                :name="contact.consultingSettings.has_active_user ? 'mib-check-circle-1' : 'mib-information-circle'"
                                color="text-secondary"
                                class="q-mr-xs"
                            />
                            <span class="additional-info">{{ $tc('views.contact.settings.consulting-settings.notification-settings.user-linked-to-consultant', contact.consultingSettings.has_active_user ? 1 : 0) }}</span>
                        </li>
                        <li>
                            <q-icon
                                :name="notificationSettingsInfo.numberOfEmailAddresses > 0 ? 'mib-check-circle-1' : 'mib-alert-triangle'"
                                :color="notificationSettingsInfo.numberOfEmailAddresses > 0 ? 'text-secondary' : notificationSettingsInfo.type"
                                class="q-mr-xs"
                            />
                            <span :class="notificationSettingsInfo.numberOfEmailAddresses > 0 ? 'additional-info' : null">{{ $tc('views.contact.settings.consulting-settings.notification-settings.email-address-added', notificationSettingsInfo.numberOfEmailAddresses) }}</span>
                        </li>
                        <li>
                            <q-icon
                                :name="notificationSettingsInfo.numberOfAreasWithoutAssignedEmailAddress > 0 ? 'mib-alert-triangle' : 'mib-check-circle-1'"
                                :color="notificationSettingsInfo.numberOfAreasWithoutAssignedEmailAddress > 0 ? notificationSettingsInfo.type : 'text-secondary'"
                                class="q-mr-xs"
                            />
                            <span :class="notificationSettingsInfo.numberOfAreasWithoutAssignedEmailAddress > 0 ? null : 'additional-info'">{{ $tc('views.contact.settings.consulting-settings.notification-settings.no-email-address-selected-for-area', notificationSettingsInfo.numberOfAreasWithoutAssignedEmailAddress) }}</span>
                        </li>
                    </ul>
                </template>
            </info-box>
        </div>

        <grid-card
            :columns="{ xs: 12, md: 6, xl: 4 }"
            :heading="$tc('common.email-address.email-address', 2)"
            content-class="q-gutter-y-sm"
        >
            <div class="row">
                <div
                    v-for="(emailAddress) in contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses"
                    :key="`email-${emailAddress.id}`"
                    class="col-xs-12 q-mb-md"
                >
                    <div class="row q-col-gutter-sm">
                        <div class="col-xs-9">
                            <fake-input-field
                                class="q-mb-sm"
                                data-test="fake-input:default-email-address"
                            >{{ emailAddress.email }}</fake-input-field>
                        </div>

                        <div class="col-xs-3 q-gutter-xs q-pb-sm text-center content-center">
                            <q-btn
                                color="primary"
                                size="sm"
                                flat
                                round
                                icon="mib-pencil"
                                data-test="btn:edit-email-address"
                                @click="triggerUpdateItem(emailAddress)"
                            >
                                <q-tooltip :delay="1000" :offset="[0, 10]">{{ $tc('common.email-address.edit-email-address', 1) }}</q-tooltip>
                            </q-btn>

                            <q-btn
                                color="negative"
                                size="sm"
                                flat
                                round
                                icon="mib-bin"
                                data-test="btn:delete-email-address"
                                @click="triggerDeleteItem(emailAddress)"
                            >
                                <q-tooltip :delay="1000" :offset="[0, 10]">{{ $tc('common.email-address.delete-email-address', 1) }}</q-tooltip>
                            </q-btn>
                        </div>
                    </div>
                </div>

                <div class="col-xs-12">
                    <base-button
                        primary-button
                        outline
                        data-test="btn:add-email-address"
                        @click="triggerCreateItem"
                    >{{ $tc('common.email-address.add-email-address', 1) }}
                    </base-button>
                </div>
            </div>
        </grid-card>

        <grid-card
            :columns="{ xs: 12, md: 6, xl: 4 }"
            :heading="$tc('views.contact.settings.consulting-settings.notification-settings.notification-settings', 1)"
            :heading-info="$tc('views.contact.settings.consulting-settings.notification-settings.email-address-per-area--info', 1)"
            content-class="q-gutter-y-sm"
        >
            <info-box
                v-if="!contact.consultingSettings.has_active_user"
                :type="notificationEmittersWithoutAssignedEmailAddressesCount === 0 ? 'positive' : !contact.consultingSettings.has_active_user ? 'warning' : 'info'"
            >
                <p>{{ $tc('views.contact.settings.consulting-settings.notification-settings.no-email-address-selected-for-area', notificationEmittersWithoutAssignedEmailAddressesCount) }}</p>
            </info-box>

            <q-card-section
                v-for="(emailId, notificationEmitter) in notificationEmitters"
                :key="`notificationEmitter-${notificationEmitter}`"
                class="q-pa-none"
            >
                <base-select
                    v-model="notificationEmitters[notificationEmitter]"
                    :options="notificationEmitterEmailOptions"
                    :label="$tc(`views.contact.settings.consulting-settings.notification-settings.area.${kebabCase(notificationEmitter)}`, 1)"
                    clearable
                    data-test="select:notification-email-address"
                >
                </base-select>
            </q-card-section>

            <template v-slot:actions>
                <div class="row">
                    <div class="col-xs-12">
                        <base-button
                            primary-button
                            data-test="btn:add-email-address"
                            :disable="!notificationEmitterEmailOptions.length || !notificationSettingsHasChanges"
                            @click="saveNotificationSettings"
                        >{{ $tc('common.term.save', 1) }}</base-button>
                        <p v-if="notificationSettingsHasChanges" class="display-inline text-secondary q-ml-sm"><q-icon name="mib-information-circle" class="q-mr-xs" /> {{ $tc('common.term.unsaved-change--info', 2) }}</p>
                    </div>
                </div>
            </template>
        </grid-card>

        <!-- Create item -->
        <form-dialog
            v-if="state.currentMode === ViewMode.CREATE"
            ref="createItemDialog"
            :form="emailAddressForm"
            :initial-data="initialFormData"
            @submit="handleCreateItem"
            @cancel="cancelAction"
        >
            <template v-slot:title>{{ $tc('common.email-address.add-email-address', 1) }}</template>
        </form-dialog>

        <!-- Update item -->
        <form-dialog
            v-if="state.currentMode === ViewMode.UPDATE && state.currentItem"
            ref="updateItemDialog"
            :form="emailAddressForm"
            :initial-data="initialFormData"
            @submit="handleUpdateItem"
            @cancel="cancelAction"
        >
            <template v-slot:title>{{ $tc('common.email-address.edit-email-address', 1) }}</template>
        </form-dialog>

        <!-- Delete item -->
        <form-dialog
            v-if="state.currentMode === ViewMode.DELETE && state.currentItem"
            ref="deleteItemDialog"
            :submit-button-label="$t('common.term.confirm')"
            :double-confirm="!!state.currentItem.notificationEmitters.length"
            @submit="handleDeleteItem"
            @cancel="cancelAction"
        >
            <template v-slot:title>{{ $tc('common.email-address.delete-email-address', 1) }}</template>
            <template v-slot:default>
                <i18n v-if="state.currentItem" path="common.term.delete-item--confirm" tag="p">
                    <template v-slot:item><b>{{ state.currentItem.email }}</b></template>
                </i18n>

                <info-box
                    v-if="state.currentItem.notificationEmitters.length"
                    :type="contact.consultingSettings.has_active_user ? 'info' : 'warning'"
                >
                    <i18n :path="`views.contact.settings.consulting-settings.notification-settings.email-address-is-assigned-to-area--temp-component-interpolation-count${state.currentItem.notificationEmitters.length === 1 ? '-1' : null}`" tag="p">
                        <template v-slot:area><b>{{ textList(state.currentItem.notificationEmitters.map(area => $t(`views.contact.settings.consulting-settings.notification-settings.area.${kebabCase(area)}`))) }}</b></template>
                    </i18n>

                    <p v-if="contact.consultingSettings.has_active_user">{{ $tc('views.contact.settings.consulting-settings.notification-settings.delete-email-address--info--default-email-address', state.currentItem.notificationEmitters.length) }}</p>
                    <p v-else>{{ $tc('views.contact.settings.consulting-settings.notification-settings.delete-email-address--info--no-notifications-sent', state.currentItem.notificationEmitters.length) }}</p>
                </info-box>
            </template>
        </form-dialog>
    </div>
</template>

<script>
import { useI18n } from '@/composables/i18n'
import { useQuasar } from '@/composables/quasar'
import { ConsultantNotificationEmitter } from '@/enums/graphql'
import { computed, reactive, ref } from 'vue'
import { useViewModeManager } from '@/composables/viewModeManager'
import BaseSelect from '@/components/form/BaseSelect.vue'
import FakeInputField from '@/components/FakeInputField.vue'
import FormDialog from '@/libs/form/components/FormDialog'
import { Form, FormField } from '@/libs/form'
import { ConsultantNotificationEmailAddressService } from '@/services/consultantNotificationEmailAddress'
import { kebabCase } from 'lodash'
import { textList } from '@/helpers'

export default {
    name: 'ConsultantNotificationSettings',
    methods: { textList, kebabCase },
    components: {
        FakeInputField,
        BaseSelect,
        FormDialog,
    },
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    setup (props, context) {
        // Composables
        const $q = useQuasar()
        const { tc } = useI18n()

        const {
            // Static
            ViewMode,

            // Data
            state,

            // Template refs
            createItemDialog,
            updateItemDialog,
            deleteItemDialog,

            // Methods
            triggerCreateItem,
            triggerUpdateItem,
            triggerDeleteItem,
            cancelAction,
        } = useViewModeManager(context)

        // Form
        const emailAddressForm = new Form()
        emailAddressForm.addField(new FormField('emailAddress', {
            label: 'common.email-address.email-address',
            inputType: 'text',
            validators: [
                { 'type': 'required' },
                { 'type': 'email' },
            ],
        }))
        const initialFormData = computed(() => {
            if (state.currentItem) {
                return {
                    emailAddress: state.currentItem.email,
                }
            }
            return {
                emailAddress: '',
            }
        })

        // Create
        function handleCreateItem (formData) {
            props.contact.consultingSettings.consultantNotificationSettings.addConsultantNotificationEmailAddress({
                consultantId: props.contact.consultingSettings.contact_id,
                consultantNotificationEmailAddress: {
                    email: formData.emailAddress,
                    notificationEmitters: [],
                },
            }).then(() => {
                cancelAction()
                $q.notify({
                    type: 'positive',
                    message: tc('common.notifications.consulting-settings.notification-settings.email-address-added-success', 1),
                })
            }).catch(error => {
                createItemDialog.value.showFormErrorMessage(error)
                createItemDialog.value.resetFormSubmitStatus()
            })
        }

        // Update
        function handleUpdateItem (formData) {
            state.currentItem.update({
                email: formData.emailAddress,
                notificationEmitters: state.currentItem.notificationEmitters,
            })
                .then(() => {
                    cancelAction()
                    $q.notify({
                        type: 'positive',
                        message: tc('common.notifications.consulting-settings.notification-settings.email-address-updated-success', 1),
                    })
                })
                .catch(error => {
                    updateItemDialog.value.showFormErrorMessage(error)
                    updateItemDialog.value.resetFormSubmitStatus()
                })
        }

        // Delete
        function handleDeleteItem (formData) {
            state.currentItem.delete(formData.doubleConfirmation)
                .then(() => {
                    props.contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses = props.contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses.filter(emailAddress => emailAddress.id !== state.currentItem.id)
                    setNotificationEmitters()
                    updateOriginalEmittersState()
                    cancelAction()
                    $q.notify({
                        type: 'positive',
                        message: tc('common.notifications.consulting-settings.notification-settings.email-address-deleted-success', 1),
                    })
                })
                .catch(error => {
                    deleteItemDialog.value.showFormErrorMessage(error)
                    deleteItemDialog.value.resetFormSubmitStatus()
                })
        }

        // Notification emitters
        const initialNotificationEmitters = {}
        Object.values(ConsultantNotificationEmitter).forEach(notificationEmitter => {
            initialNotificationEmitters[notificationEmitter] = null
        })
        const notificationEmitters = reactive(initialNotificationEmitters)

        function setNotificationEmitters () {
            // Reset all emitters to null.
            Object.keys(notificationEmitters).forEach(key => {
                notificationEmitters[key] = null
            })

            // Set emitters based on current email addresses.
            props.contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses.forEach(emailAddress => {
                if (emailAddress.notificationEmitters && emailAddress.notificationEmitters.length > 0) {
                    emailAddress.notificationEmitters.forEach(emitter => {
                        notificationEmitters[emitter] = emailAddress.id
                    })
                }
            })
        }
        setNotificationEmitters()

        // Store the initial state after loading.
        let originalEmitterState = ref(JSON.stringify(notificationEmitters))

        // Update the original state after saving.
        function updateOriginalEmittersState () {
            originalEmitterState.value = JSON.stringify(notificationEmitters)
        }

        // Computed property to check for changes.
        const notificationSettingsHasChanges = computed(() => {
            return JSON.stringify(notificationEmitters) !== originalEmitterState.value
        })

        // Computed property to count emitters without email addresses.
        const notificationEmittersWithoutAssignedEmailAddressesCount = computed(() => {
            return Object.values(notificationEmitters).filter(value => value === null).length
        })

        const notificationEmitterEmailOptions = computed(() => {
            return props.contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses.map(emailAddress => {
                return {
                    label: emailAddress.email,
                    value: emailAddress.id,
                }
            })
        })

        // Computed property to get the appropriate info text (and config).
        const notificationSettingsInfo = computed(() => {
            const numberOfAreas = Object.keys(notificationEmitters).length
            const numberOfEmailAddresses = Object.keys(props.contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses).length
            const numberOfAreasWithoutAssignedEmailAddress = notificationEmittersWithoutAssignedEmailAddressesCount.value

            let notificationsSent = 'all'
            if (!props.contact.consultingSettings.has_active_user) {
                if (numberOfAreasWithoutAssignedEmailAddress > 0) {
                    notificationsSent = 'some'

                    if (numberOfAreas - numberOfAreasWithoutAssignedEmailAddress <= 0) {
                        notificationsSent = 'none'
                    }
                }
            }

            let type = ''
            switch (notificationsSent) {
                case 'none':
                    type = 'negative'
                    break
                case 'some':
                    type = 'warning'
                    break
                case 'all':
                default:
                    type = !props.contact.consultingSettings.has_active_user ? 'positive' : 'info'
                    break
            }

            return {
                type: type,
                numberOfEmailAddresses: numberOfEmailAddresses,
                numberOfAreasWithoutAssignedEmailAddress: numberOfAreasWithoutAssignedEmailAddress,
                notificationsSent: notificationsSent,
            }
        })

        function saveNotificationSettings () {
            const emailAddressEmitters = {}

            // Collect emitters for each email address.
            Object.entries(notificationEmitters).forEach(([emitter, emailAddressId]) => {
                if (emailAddressId) {
                    if (!emailAddressEmitters[emailAddressId]) emailAddressEmitters[emailAddressId] = []
                    emailAddressEmitters[emailAddressId].push(emitter)
                }
            })

            // Collect all email addresses that need to be updated.
            const emailAddressesToUpdate = []

            props.contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses.forEach(emailAddress => {
                // Get the current emitters for this email address.
                const currentEmitters = emailAddress.notificationEmitters || []
                // Get the new emitters for this email address.
                const newEmitters = emailAddressEmitters[emailAddress.id] || []

                // Only add to update list if emitters have changed.
                if (JSON.stringify(currentEmitters.sort()) !== JSON.stringify(newEmitters.sort())) {
                    emailAddressesToUpdate.push({
                        id: emailAddress.id,
                        email: emailAddress.email,
                        notificationEmitters: newEmitters,
                    })
                }
            })

            // If there are no changes, return early
            if (emailAddressesToUpdate.length === 0) {
                return Promise.resolve()
            }

            // Use bulkUpdate to update all email addresses at once.
            return ConsultantNotificationEmailAddressService.bulkUpdate(emailAddressesToUpdate)
                .then(updatedEmailAddresses => {
                    // Update the local email address objects with the updated data.
                    updatedEmailAddresses.forEach(updatedEmailAddress => {
                        const localEmailAddress = props.contact.consultingSettings.consultantNotificationSettings.notificationEmailAddresses.find(
                            emailAddress => emailAddress.id === updatedEmailAddress.id
                        )
                        if (localEmailAddress) {
                            Object.assign(localEmailAddress, updatedEmailAddress)
                        }
                    })

                    // Refresh the notificationEmitters.
                    setNotificationEmitters()

                    // Update the original state after saving.
                    updateOriginalEmittersState()

                    $q.notify({
                        type: 'positive',
                        message: tc('common.notifications.consulting-settings.notification-settings.notification-settings-saved-success', 1),
                    })
                })
                .catch(error => {
                    $q.notify({
                        type: 'negative',
                        message: tc('common.notifications.consulting-settings.notification-settings.notification-settings-saved-error', 1),
                    })
                })
        }

        return {
            // Static
            ViewMode,
            emailAddressForm,

            // Data
            state,

            // Computed
            initialFormData,
            notificationSettingsHasChanges,
            notificationEmittersWithoutAssignedEmailAddressesCount,

            // Template refs
            createItemDialog,
            updateItemDialog,
            deleteItemDialog,

            // Methods
            triggerCreateItem,
            triggerUpdateItem,
            triggerDeleteItem,
            handleCreateItem,
            handleUpdateItem,
            handleDeleteItem,
            saveNotificationSettings,
            cancelAction,

            // Notification emitters
            notificationEmitters,
            notificationEmitterEmailOptions,
            notificationSettingsInfo,
        }
    },
}
</script>
